import type { ReactNode } from 'react';
import { TechPattern } from '@/components/brand/TechPattern';

interface SectionProps {
  children: ReactNode;
  tone?: 'light' | 'muted' | 'blue' | 'dark' | 'gradient';
  size?: 'sm' | 'base';
  pattern?: boolean;
  className?: string;
  id?: string;
}

/**
 * Owns vertical rhythm and surface tone for every page section.
 *
 * Centralising the spacing here is what stops the rhythm drifting page to page:
 * 120px desktop, 96px tablet, 72px mobile, defined once.
 */
export function Section({
  children,
  tone = 'light',
  size = 'base',
  pattern = false,
  className = '',
  id,
}: SectionProps) {
  const tones = {
    light: 'bg-surface text-ink',
    muted: 'bg-surface-muted text-ink',
    /* A blue-tinted band, so alternating sections are not all the same grey. */
    blue: 'bg-surface-blue text-ink',
    /*
      The dark bands carry a gradient rather than one flat navy. A single fill
      across a full-width section reads as a block of paint; the shift from
      the darkest vignette through the base navy gives it depth, which is what
      makes the dark sections feel deliberate against the light ones.
    */
    dark: 'bg-linear-to-b from-ink-3 via-ink to-ink-3 text-white',
    gradient: 'bg-linear-to-br from-ink-3 via-ink to-brand text-white',
  } as const;

  const pad =
    size === 'sm'
      ? 'py-section-sm md:py-section-md'
      : 'py-section-sm md:py-section-md lg:py-section';

  const isDark = tone === 'dark' || tone === 'gradient';

  return (
    <section id={id} className={`relative overflow-hidden ${tones[tone]} ${pad} ${className}`}>
      {pattern && <TechPattern opacity={0.06} />}

      {/*
        A single warm bloom in the corner of every dark band. It is barely
        visible on its own, but it stops the navy reading as flat paint and
        ties the dark sections to the accent used everywhere else.
        pointer-events-none so it never intercepts a click.
      */}
      {isDark && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-1/3 -right-1/4 h-[60rem] w-[60rem] rounded-full opacity-[0.07]"
          style={{ background: 'radial-gradient(closest-side, var(--color-accent), transparent)' }}
        />
      )}

      <div className="shell relative z-10">{children}</div>
    </section>
  );
}
